import moment from 'moment-timezone';

/**
 * Preset identifiers for the date range selector.
 * Regular presets compute a range; custom presets open dialogs for user input.
 */
export type DateRangePresetId =
  | 'today'
  | 'yesterday'
  | 'lastHour'
  | 'last24Hours'
  | 'last7Days'
  | 'last14Days'
  | 'last30Days'
  | 'last90Days'
  | 'last180Days'
  | 'lastWeek'
  | 'lastMonth'
  | 'thisWeek'
  | 'thisMonth'
  | 'yearToDate'
  | 'customSingleDate'
  | 'fromCustomDateUntilNow'
  | 'customFixedDateRange'
  | 'customRange';

export interface LastNHoursPreset {
  type: 'lastNHours';
  hours: number;
}

/** Hybrid preset contract: legacy string ids + dynamic object presets. */
export type DateRangePreset = DateRangePresetId | LastNHoursPreset;

/** Presets that open a dialog instead of computing a range immediately. */
export const CUSTOM_PRESET_IDS: DateRangePresetId[] = [
  'customSingleDate',
  'fromCustomDateUntilNow',
  'customFixedDateRange',
  'customRange',
];

export function isLastNHoursPreset(preset: DateRangePreset): preset is LastNHoursPreset {
  return typeof preset === 'object' && preset.type === 'lastNHours';
}

/** Normalizes a hybrid preset into a stable token for equality and persistence. */
export function serializePresetToken(preset: DateRangePreset): string {
  if (isLastNHoursPreset(preset)) {
    return `type=lastNHours;hours=${preset.hours}`;
  }

  return preset;
}

/** Parses preset tokens produced by {@link serializePresetToken}. */
export function parsePresetToken(token: string): DateRangePreset | null {
  const trimmed = token.trim();
  if (!trimmed) {
    return null;
  }

  if (VALID_PRESET_ID_SET.has(trimmed as DateRangePresetId)) {
    return trimmed as DateRangePresetId;
  }

  if (trimmed.startsWith('type=')) {
    const typeToken = trimmed.slice('type='.length);
    if (VALID_PRESET_ID_SET.has(typeToken as DateRangePresetId)) {
      return typeToken as DateRangePresetId;
    }
  }

  const match = /^type=lastNHours;hours=(\d+)$/.exec(trimmed);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  if (!Number.isInteger(hours) || hours <= 0) {
    return null;
  }

  return { type: 'lastNHours', hours };
}

/** Compares two hybrid presets by stable token. */
export function isSamePreset(
  a: DateRangePreset | null | undefined,
  b: DateRangePreset | null | undefined,
): boolean {
  if (a == null || b == null) {
    return false;
  }

  return serializePresetToken(a) === serializePresetToken(b);
}

export function isCustomPreset(preset: DateRangePreset): boolean {
  if (isLastNHoursPreset(preset)) {
    return false;
  }

  return CUSTOM_PRESET_IDS.includes(preset);
}

export interface DateRangeValue {
  from: Date;
  to: Date;
}

/**
 * Computes the date range for a given preset in the specified timezone.
 * Uses moment-timezone for DST-safe start/end of day, week, month, year.
 * Does not handle custom presets (customSingleDate, fromCustomDateUntilNow, customFixedDateRange);
 * callers must use dialog results for those.
 */
export function getPresetRange(
  preset: DateRangePreset,
  timezone: string,
  now?: Date,
): DateRangeValue | null {
  if (isCustomPreset(preset)) {
    return null;
  }

  const ref = now ? moment(now).tz(timezone) : moment().tz(timezone);

  if (isLastNHoursPreset(preset)) {
    if (!Number.isInteger(preset.hours) || preset.hours <= 0) {
      return null;
    }

    const to = ref.toDate();
    const from = ref.clone().subtract(preset.hours, 'hours').toDate();
    return { from, to };
  }

  switch (preset) {
    case 'today': {
      const from = ref.clone().startOf('day').toDate();
      const to = ref.clone().endOf('day').toDate();
      return { from, to };
    }

    case 'yesterday': {
      const start = ref.clone().subtract(1, 'day').startOf('day');
      const from = start.toDate();
      const to = start.clone().endOf('day').toDate();
      return { from, to };
    }

    case 'lastHour': {
      const to = ref.toDate();
      const from = ref.clone().subtract(1, 'hour').toDate();
      return { from, to };
    }

    case 'last24Hours': {
      const to = ref.toDate();
      const from = ref.clone().subtract(24, 'hours').toDate();
      return { from, to };
    }

    case 'last7Days': {
      const to = ref.clone().endOf('day').toDate();
      const from = ref.clone().subtract(6, 'days').startOf('day').toDate();
      return { from, to };
    }

    case 'last14Days': {
      const to = ref.clone().endOf('day').toDate();
      const from = ref.clone().subtract(13, 'days').startOf('day').toDate();
      return { from, to };
    }

    case 'last30Days': {
      const to = ref.clone().endOf('day').toDate();
      const from = ref.clone().subtract(29, 'days').startOf('day').toDate();
      return { from, to };
    }

    case 'last90Days': {
      const to = ref.clone().endOf('day').toDate();
      const from = ref.clone().subtract(89, 'days').startOf('day').toDate();
      return { from, to };
    }

    case 'last180Days': {
      const to = ref.clone().endOf('day').toDate();
      const from = ref.clone().subtract(179, 'days').startOf('day').toDate();
      return { from, to };
    }

    case 'lastWeek': {
      const start = ref.clone().subtract(1, 'week').startOf('week');
      const from = start.toDate();
      const to = start.clone().endOf('week').toDate();
      return { from, to };
    }

    case 'lastMonth': {
      const start = ref.clone().subtract(1, 'month').startOf('month');
      const from = start.toDate();
      const to = start.clone().endOf('month').toDate();
      return { from, to };
    }

    case 'thisWeek': {
      const from = ref.clone().startOf('week').toDate();
      const to = ref.clone().endOf('week').toDate();
      return { from, to };
    }

    case 'thisMonth': {
      const from = ref.clone().startOf('month').toDate();
      const to = ref.clone().endOf('month').toDate();
      return { from, to };
    }

    case 'yearToDate': {
      const from = ref.clone().startOf('year').toDate();
      const to = ref.clone().endOf('day').toDate();
      return { from, to };
    }

    default:
      return null;
  }
}

/** Ordered list of preset ids for the preset menu (excluding custom; those are appended). */
export const PRESET_MENU_ORDER: DateRangePresetId[] = [
  'today',
  'yesterday',
  'lastHour',
  'last24Hours',
  'last7Days',
  'last14Days',
  'last30Days',
  'last90Days',
  'last180Days',
  'lastWeek',
  'lastMonth',
  'thisWeek',
  'thisMonth',
  'yearToDate',
  'customSingleDate',
  'fromCustomDateUntilNow',
  'customFixedDateRange',
];

/**
 * Presets accepted by serialization/parsing.
 * Includes hidden presets that are not shown in the selector menu.
 */
const ALL_PRESET_IDS: DateRangePresetId[] = [...PRESET_MENU_ORDER, 'customRange'];
const VALID_PRESET_ID_SET = new Set<DateRangePresetId>(ALL_PRESET_IDS);

/**
 * Preserves order. Keeps custom presets (dialogs enforce the bound). Drops non-custom presets
 * whose `getPresetRange` `from` is before `minDate` (compared as absolute UTC timestamps).
 * `timezone` is only used to compute each preset’s range, not to reinterpret `minDate`.
 */
export function filterPresetIdsByMinDate(
  ids: DateRangePreset[],
  minDate: Date,
  timezone: string,
): DateRangePreset[] {
  const minBoundMs = minDate.getTime();
  return ids.filter((id) => {
    if (isCustomPreset(id)) {
      return true;
    }
    const range = getPresetRange(id, timezone);
    if (!range) {
      return false;
    }
    return range.from.getTime() >= minBoundMs;
  });
}
